"use client";

import publicNavLinks from "../publicNavLinks";
import { LogoLinkChakra } from "./LogoLink";
import { Box, HStack } from "@chakra-ui/react";
import { navbarConfig } from "@/shared/navigation";
import PublicNavbarBurgerMenu from "./PublicNavbarBurgerMenu";
import PublicNavbarMobileBehavior from "./PublicNavbarMobileBehavior";
import { memo } from "react";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { getPublicAppEntry } from "./publicAppEntry";
import { useAuth } from "@/state/globalStore";
import { PublicActionButton, PublicLink } from "@/shared/public-ui";

interface PublicNavbarProps {
  isAuthenticatedServerSide: boolean;
}

function PublicNavbar({
  isAuthenticatedServerSide = false,
}: PublicNavbarProps) {
  const pathname = usePathname();
  const { user } = useAuth();
  const isAuthenticated = !!user || !!isAuthenticatedServerSide;
  const publicAppEntry = getPublicAppEntry({
    isAuthenticated,
    pathname,
  });

  return (
    <Box
      pt={{ base: "6", md: "10", lg: "12" }}
      px={{ base: "2", md: "8", lg: "16", xl: "24" }}
    >
      <Box
        as="nav"
        data-public-navbar-mobile
        display={{ base: "flex", md: "none" }}
        justifyContent="space-between"
        px="4"
        h={navbarConfig.NAVBAR_HEIGHT_MOBILE}
        minH={navbarConfig.NAVBAR_HEIGHT_MOBILE}
        w="full"
        alignItems="center"
        position="fixed"
        left={0}
        right={0}
        top={0}
        transform="translateY(0)"
        transition={`transform ${navbarConfig.NAVBAR_HIDE_TRANSITION_SEC}s ease-in-out, opacity ${navbarConfig.NAVBAR_HIDE_TRANSITION_SEC}s ease-in-out`}
        zIndex="sticky"
        backgroundColor="moveon.shell.bg"
        borderBottomWidth="1"
        borderColor="moveon.shell.border"
        backdropFilter="saturate(180%) blur(6px)"
      >
        <LogoLinkChakra whichNavbar="public" />

        <PublicNavbarBurgerMenu
          navLinks={publicNavLinks}
          isAuthenticatedServerSide={isAuthenticatedServerSide}
        />
      </Box>

      <Box
        display={{ base: "block", md: "none" }}
        h={navbarConfig.NAVBAR_HEIGHT_MOBILE}
        aria-hidden
      />

      <Box
        as="nav"
        display={{ base: "none", md: "flex" }}
        justifyContent="space-between"
        alignItems="center"
        gap="6"
        p="3"
        w="full"
        maxW="7xl"
        mx="auto"
      >
        <PublicNavbarMobileBehavior
          lock={navbarConfig.NAVBAR_LOCK}
          hideDelayMs={navbarConfig.NAVBAR_HIDE_DELAY_MS}
          heightUnits={navbarConfig.NAVBAR_HEIGHT_MOBILE}
          thresholdPx={navbarConfig.NAVBAR_SCROLL_THRESHOLD_PX}
          velocityThreshold={navbarConfig.NAVBAR_VELOCITY_THRESHOLD}
          velocitySmoothing={navbarConfig.VELOCITY_SMOOTHING}
          transitionSec={navbarConfig.NAVBAR_HIDE_TRANSITION_SEC}
        />
        <HStack gap="2" align="center">
          <LogoLinkChakra whichNavbar="public" />
        </HStack>

        <HStack gap="3" align="center">
          <HStack gap={{ md: "1", lg: "2" }} align="center">
            {publicNavLinks.map((link) => {
              const isActive =
                pathname === link.href || pathname?.startsWith(`${link.href}/`);

              return (
                <PublicLink
                  key={link.href}
                  variant="nav"
                  asChild
                  px={{ md: "3", lg: "4" }}
                  py="2"
                  borderRadius="full"
                  fontWeight={isActive ? "semibold" : "medium"}
                  color={isActive ? "moveon.primary" : "moveon.shell.mutedFg"}
                  bg={isActive ? "moveon.shell.activeBg" : "transparent"}
                  _hover={{
                    textDecoration: "none",
                    bg: "moveon.shell.hoverBg",
                    color: "moveon.primary",
                  }}
                  transition="all 0.2s"
                >
                  <NextLink href={link.href}>{link.title}</NextLink>
                </PublicLink>
              );
            })}
          </HStack>

          <PublicActionButton
            asChild
            size="sm"
            borderRadius="full"
            px="5"
          >
            <NextLink href={publicAppEntry.href}>{publicAppEntry.label}</NextLink>
          </PublicActionButton>
        </HStack>
      </Box>
    </Box>
  );
}

export default memo(PublicNavbar);
